import * as React from 'react';
import {
  EventType,
  InteractionRequiredAuthError,
  InteractionStatus,
  NavigationClient,
  type AccountInfo,
  type AuthenticationResult,
  type EventMessage,
  type IPublicClientApplication,
  type NavigationOptions
} from '@azure/msal-browser';
import { MsalProvider, useMsal } from '@azure/msal-react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';

import { setAuthTokenProvider } from '@cdf/api-client-react';
import { interactiveSignInUsesPopup } from '@/lib/display-mode';
import { host } from '@/lib/embed';
import {
  externalEntraConfigurationComplete,
  getAuthProviderConfig,
  persistAuthProvider,
  silentTokenOptions,
  type AuthProviderKind
} from '@/lib/auth-config';
import { classifyAuthError } from '@/lib/auth-errors';
import {
  isMicrosoftConsentKind,
  MICROSOFT_CONSENT_SCOPES,
  MicrosoftConsentRequiredError,
  type MicrosoftConsentKind,
  type MicrosoftConsentResult
} from '@/lib/microsoft-consent';
import {
  acquireAzureDevOpsAccessToken,
  AZURE_DEVOPS_SCOPES,
  AzureDevOpsTokenError
} from '@/lib/azure-devops/auth';

/**
 * Plugin-aware authentication provider and the single MSAL lifecycle owner.
 *
 * - Auth plugin disabled  → renders children directly; useAuth() reports
 *   { enabled: false } and the app shows an honest "local dev" state.
 * - Auth plugin ready     → uses the entry-point-initialized MSAL client.
 *
 * Embedded-first contract (when a redirect bridge is configured):
 * - Inside an iframe (Foundry Preview, SharePoint) the app runs one silent
 *   sign-in per page load through the hidden bridge iframe.
 * - Top-level pages and same-origin frames sign in with a popup.
 * - Cross-origin frames hand off one sign-in to a top-level tab; the frame
 *   never navigates to Microsoft.
 * - Full-page redirects (fallback, handoff tab) always use the root redirect.
 */

export interface AuthContextValue {
  enabled: boolean;
  ready: boolean;
  isAuthenticated: boolean;
  account: { name: string; email: string | null; stableId?: string } | null;
  issue: AuthIssue | null;
  isSigningIn: boolean;
  isSigningOut: boolean;
  recoveryUrl: string;
  provider: AuthProviderKind;
  externalProviderAvailable: boolean;
  getAccessToken: (scopes: readonly string[]) => Promise<string | null>;
  getAzureDevOpsAccessToken: () => Promise<string>;
  /** Ask the signed-in person to approve a named set of extra Microsoft permissions. Call from a click. */
  requestMicrosoftConsent: (kind: MicrosoftConsentKind) => Promise<MicrosoftConsentResult>;
  login: (provider?: AuthProviderKind) => Promise<void>;
  logout: () => Promise<void>;
  clearIssue: () => void;
}

export interface AuthIssue {
  kind: 'popupBlocked' | 'sessionExpired' | 'unexpected';
  message: string;
}

const AuthContext = React.createContext<AuthContextValue>({
  enabled: false,
  ready: true,
  isAuthenticated: false,
  account: null,
  issue: null,
  isSigningIn: false,
  isSigningOut: false,
  recoveryUrl: '',
  provider: 'workforce',
  externalProviderAvailable: externalEntraConfigurationComplete,
  getAccessToken: async () => null,
  getAzureDevOpsAccessToken: async () => {
    throw new AzureDevOpsTokenError('authentication_expired');
  },
  requestMicrosoftConsent: async () => 'failed',
  login: async () => undefined,
  logout: async () => undefined,
  clearIssue: () => undefined
});

export function useAuth(): AuthContextValue {
  return React.useContext(AuthContext);
}

function accountDisplay(account: AccountInfo): NonNullable<AuthContextValue['account']> {
  return {
    name: account.name ?? account.username,
    email: account.username || null,
    stableId: JSON.stringify([account.environment, account.tenantId, account.localAccountId, account.homeAccountId])
  };
}

type HandoffAction = 'login' | 'logout' | 'devopsConsent' | 'graphConsent';

/** A single-use top-level intent on this app's own root. */
function topLevelHandoffUrl(
  action: HandoffAction,
  options: { returnTo?: string; provider?: AuthProviderKind; consent?: MicrosoftConsentKind } = {}
): string {
  const url = new URL('/', window.location.origin);
  url.searchParams.set('authAction', action);
  if (options.provider) url.searchParams.set('authProvider', options.provider);
  if (options.consent) url.searchParams.set('authConsent', options.consent);
  if (options.returnTo) url.searchParams.set('returnTo', options.returnTo);
  return url.toString();
}

function currentLocation(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function safeLocalPath(value: string | null): string {
  return value?.startsWith('/') && !value.startsWith('//') && !value.startsWith('/\\') ? value : '/';
}

/**
 * After a full-page Microsoft redirect returns to the registered root, MSAL
 * moves to the page that started sign-in. Routing that move through React
 * Router avoids a second full page load.
 */
export class RouterNavigationClient extends NavigationClient {
  private readonly navigate: NavigateFunction;

  constructor(navigate: NavigateFunction) {
    super();
    this.navigate = navigate;
  }

  override async navigateInternal(url: string, options: NavigationOptions): Promise<boolean> {
    const target = new URL(url, window.location.origin);
    if (target.origin !== window.location.origin) return super.navigateInternal(url, options);
    this.navigate(`${target.pathname}${target.search}${target.hash}`, { replace: options.noHistory });
    return false;
  }
}

interface E2eBrowserIdentity {
  name: string;
  email: string;
}

function readE2eBrowserIdentity(): E2eBrowserIdentity | null {
  if (!import.meta.env.DEV || typeof window === 'undefined') return null;
  const candidate = (window as Window & { __CDF_E2E_IDENTITY__?: unknown }).__CDF_E2E_IDENTITY__;
  if (!candidate || typeof candidate !== 'object') return null;
  const { name, email } = candidate as Partial<E2eBrowserIdentity>;
  return typeof name === 'string' && name.length > 0 && typeof email === 'string' && email.length > 0
    ? { name, email }
    : null;
}

function E2eAuthBridge({
  identity,
  children
}: {
  identity: E2eBrowserIdentity;
  children: React.ReactNode;
}) {
  React.useEffect(() => {
    setAuthTokenProvider(null);
    return () => setAuthTokenProvider(null);
  }, []);

  const value = React.useMemo<AuthContextValue>(() => ({
    enabled: true,
    ready: true,
    isAuthenticated: true,
    account: identity,
    issue: null,
    isSigningIn: false,
    isSigningOut: false,
    recoveryUrl: window.location.href,
    provider: 'workforce',
    externalProviderAvailable: externalEntraConfigurationComplete,
    getAccessToken: async () => null,
    getAzureDevOpsAccessToken: async () => {
      throw new AzureDevOpsTokenError('authentication_expired');
    },
    requestMicrosoftConsent: async () => 'failed',
    login: async () => undefined,
    logout: async () => undefined,
    clearIssue: () => undefined
  }), [identity]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

const POPUP_BLOCKED_TAB = 'Your browser blocked the Microsoft sign-in tab. Allow popups and try again.';

function MsalAuthBridge({
  provider,
  children
}: {
  provider: AuthProviderKind;
  children: React.ReactNode;
}) {
  const { instance, accounts, inProgress } = useMsal();
  const providerConfig = getAuthProviderConfig(provider);
  if (!providerConfig) {
    throw new Error('Selected identity provider configuration is unavailable.');
  }
  const { apiTokenRequest, loginRequest, redirectUri, popupRedirectUri: bridge } = providerConfig;
  // iOS Home Screen apps cannot receive a popup's response, so they sign in
  // with the root redirect; silent sign-in through the bridge is unaffected.
  const popupMode = interactiveSignInUsesPopup(providerConfig.interactionMode, Boolean(bridge));
  const silentOptions = React.useMemo(() => silentTokenOptions(providerConfig), [providerConfig]);

  const [account, setAccount] = React.useState<AccountInfo | null>(null);
  const [ready, setReady] = React.useState(false);
  const [issue, setIssue] = React.useState<AuthIssue | null>(null);
  const [isSigningIn, setIsSigningIn] = React.useState(false);
  const [isSigningOut, setIsSigningOut] = React.useState(false);
  const accountRef = React.useRef<AccountInfo | null>(null);
  const suppressAccountRestore = React.useRef(false);
  const authIntentHandled = React.useRef(false);
  const tokenRecoveryAccount = React.useRef<AccountInfo | null>(null);
  const accountGeneration = React.useRef(0);
  // One synchronous lock shared by every interactive or silent sign-in call.
  const pendingInteraction = React.useRef(false);
  const silentBootstrapStarted = React.useRef(false);
  // Armed only after a top-level handoff tab opened: allows one silent retry.
  const handoffRetryArmed = React.useRef(false);
  // Set after a blocked popup (sign-in or Azure DevOps consent) so the next
  // click uses the non-popup fallback: the browser is blocking popups anyway.
  const popupFallback = React.useRef(false);

  // In frames that cannot use a popup, interaction moves to a top-level tab.
  const usesHandoff = host === 'cross-origin-frame' || (host === 'same-origin-frame' && !popupMode);

  const selectAccount = React.useCallback(
    (nextAccount: AccountInfo | null) => {
      instance.setActiveAccount(nextAccount);
      accountRef.current = nextAccount;
      setAccount(nextAccount);
    },
    [instance]
  );

  const requireUserRecovery = React.useCallback(
    (nextIssue: AuthIssue, recoveryAccount: AccountInfo) => {
      suppressAccountRestore.current = true;
      tokenRecoveryAccount.current = recoveryAccount;
      accountGeneration.current += 1;
      setAuthTokenProvider(null);
      selectAccount(null);
      setIssue(nextIssue);
      setReady(true);
    },
    [selectAccount]
  );

  const activateAccount = React.useCallback(
    (nextAccount: AccountInfo | null) => {
      const generation = ++accountGeneration.current;
      if (!nextAccount) {
        setAuthTokenProvider(null);
        selectAccount(null);
        return;
      }

      setAuthTokenProvider(async () => {
        try {
          // Background renewal never opens UI: failures become a user action.
          const result = await instance.acquireTokenSilent({
            ...silentOptions,
            scopes: apiTokenRequest.scopes,
            account: nextAccount
          });
          // The API accepts only this custom API's delegated access token.
          if (generation !== accountGeneration.current) return null;
          if (result.accessToken) return result.accessToken;
          requireUserRecovery(
            {
              kind: 'unexpected',
              message: 'Microsoft did not return a usable API access token. Please sign in again.'
            },
            nextAccount
          );
        } catch (error) {
          if (generation !== accountGeneration.current) return null;
          if (
            error instanceof InteractionRequiredAuthError ||
            classifyAuthError(error) === 'interactionRequired'
          ) {
            requireUserRecovery(
              {
                kind: 'sessionExpired',
                message: 'Your Microsoft session needs attention. Sign in again to continue.'
              },
              nextAccount
            );
          } else {
            requireUserRecovery(
              {
                kind: 'unexpected',
                message: 'A Microsoft API access token could not be obtained. Please sign in again.'
              },
              nextAccount
            );
          }
        }
        return null;
      });
      selectAccount(nextAccount);
    },
    [apiTokenRequest.scopes, instance, requireUserRecovery, selectAccount, silentOptions]
  );

  React.useEffect(() => () => setAuthTokenProvider(null), []);

  React.useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    if (ready) return;

    const cached = instance.getActiveAccount() ?? accounts[0] ?? instance.getAllAccounts()[0] ?? null;
    suppressAccountRestore.current = false;
    activateAccount(cached);
    setIssue(null);
    setReady(true);
  }, [accounts, activateAccount, inProgress, instance, ready]);

  // LOGIN_SUCCESS carries AccountInfo; ACQUIRE_TOKEN_SUCCESS carries an
  // AuthenticationResult with a nested account.
  React.useEffect(() => {
    const callbackId = instance.addEventCallback((event: EventMessage) => {
      if (event.eventType !== EventType.LOGIN_SUCCESS && event.eventType !== EventType.ACQUIRE_TOKEN_SUCCESS) return;
      const payload = event.payload as AccountInfo | AuthenticationResult | null;
      const next = payload && 'homeAccountId' in payload ? payload : payload?.account ?? null;
      // A late background success after sign-out or token failure must not
      // restore the account; only an explicit sign-in clears suppression.
      if (suppressAccountRestore.current) return;
      if (!next || accountRef.current?.homeAccountId === next.homeAccountId) return;
      tokenRecoveryAccount.current = null;
      activateAccount(next);
      setIssue(null);
    });
    return () => {
      if (callbackId) instance.removeEventCallback(callbackId);
    };
  }, [activateAccount, instance]);

  // The only ssoSilent call site. Expected failures (no Microsoft session,
  // blocked third-party cookies, several accounts) leave Sign in available.
  const runSilentSignIn = React.useCallback(() => {
    if (!bridge || pendingInteraction.current || accountRef.current) return;
    pendingInteraction.current = true;
    setIsSigningIn(true);
    void instance
      .ssoSilent({ scopes: loginRequest.scopes, redirectUri: bridge })
      .then((result) => {
        if (!result.account || accountRef.current) return;
        suppressAccountRestore.current = false;
        tokenRecoveryAccount.current = null;
        activateAccount(result.account);
        setIssue(null);
      })
      .catch(() => undefined)
      .finally(() => {
        pendingInteraction.current = false;
        setIsSigningIn(false);
      });
  }, [activateAccount, bridge, instance, loginRequest.scopes]);

  // One silent sign-in per page load when embedded with no cached account.
  React.useEffect(() => {
    if (!ready || inProgress !== InteractionStatus.None || silentBootstrapStarted.current) return;
    silentBootstrapStarted.current = true;
    if (host === 'top-level' || accountRef.current || suppressAccountRestore.current) return;
    runSilentSignIn();
  }, [inProgress, ready, runSilentSignIn]);

  // After a top-level handoff, the frame retries silent sign-in once.
  React.useEffect(() => {
    if (host === 'top-level' || !bridge) return;
    const retry = () => {
      if (!handoffRetryArmed.current || accountRef.current || document.visibilityState !== 'visible') return;
      handoffRetryArmed.current = false;
      runSilentSignIn();
    };
    window.addEventListener('focus', retry);
    document.addEventListener('visibilitychange', retry);
    return () => {
      window.removeEventListener('focus', retry);
      document.removeEventListener('visibilitychange', retry);
    };
  }, [bridge, runSilentSignIn]);

  // Single-use top-level intents opened from an embedded frame. The intent is
  // removed from the address before any MSAL call.
  React.useEffect(() => {
    if (authIntentHandled.current || host !== 'top-level' || inProgress !== InteractionStatus.None) return;

    const url = new URL(window.location.href);
    const action = url.searchParams.get('authAction');
    if (action !== 'login' && action !== 'logout' && action !== 'devopsConsent' && action !== 'graphConsent') return;

    authIntentHandled.current = true;
    const returnTo = safeLocalPath(url.searchParams.get('returnTo'));
    const consentKind = url.searchParams.get('authConsent');
    url.searchParams.delete('authAction');
    url.searchParams.delete('authProvider');
    url.searchParams.delete('authConsent');
    url.searchParams.delete('returnTo');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);

    const redirectStartPage = `${window.location.origin}${returnTo}`;
    if (action === 'login') {
      void instance
        .loginRedirect({ ...loginRequest, redirectUri, redirectStartPage })
        .catch(() => setIssue({ kind: 'unexpected', message: 'Microsoft sign-in could not be started. Please try again.' }));
    } else if (action === 'logout') {
      void instance
        .logoutRedirect({ postLogoutRedirectUri: redirectUri })
        .catch(() => setIssue({ kind: 'unexpected', message: 'Microsoft sign-out could not be started. Please try again.' }));
    } else if (action === 'graphConsent') {
      // Only a named permission set is accepted from the address.
      if (!isMicrosoftConsentKind(consentKind)) return;
      const consentAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
      const consentRequest = { scopes: [...MICROSOFT_CONSENT_SCOPES[consentKind]], redirectUri, redirectStartPage };
      const consent = consentAccount
        ? instance.acquireTokenRedirect({ ...consentRequest, account: consentAccount })
        : instance.loginRedirect(consentRequest);
      void consent.catch(() => setIssue({
        kind: 'unexpected',
        message: 'Microsoft approval could not be started. Please try again.'
      }));
    } else {
      const consentAccount = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? null;
      const consentRequest = { scopes: [...AZURE_DEVOPS_SCOPES], redirectUri, redirectStartPage };
      const consent = consentAccount
        ? instance.acquireTokenRedirect({ ...consentRequest, account: consentAccount })
        : instance.loginRedirect(consentRequest);
      void consent.catch(() => setIssue({
        kind: 'unexpected',
        message: 'Azure DevOps consent could not be started. Please try again.'
      }));
    }
  }, [inProgress, instance, loginRequest, redirectUri]);

  // MSAL synchronizes accounts across same-origin tabs. Explicit suppression
  // prevents a stale cached account from being restored after token failure
  // or sign-out.
  React.useEffect(() => {
    if (!ready || inProgress !== InteractionStatus.None || suppressAccountRestore.current) return;
    if (account) {
      if (accounts.length === 0 && instance.getAllAccounts().length === 0) activateAccount(null);
      return;
    }
    const available = accounts[0] ?? null;
    if (available) activateAccount(available);
  }, [account, accounts, activateAccount, inProgress, instance, ready]);

  const login = React.useCallback(async (requestedProvider: AuthProviderKind = provider) => {
    if (inProgress !== InteractionStatus.None || pendingInteraction.current) return;
    if (requestedProvider === 'external' && !externalEntraConfigurationComplete) return;

    const recoveryAccount = tokenRecoveryAccount.current;
    setIssue(null);
    suppressAccountRestore.current = false;
    tokenRecoveryAccount.current = null;
    persistAuthProvider(requestedProvider);

    // Switching provider needs that provider's MSAL client: start it from a
    // fresh top-level page. Frames that cannot use a popup hand off to a tab.
    const sameOriginPopupBlocked = host === 'same-origin-frame' && popupFallback.current;
    if (requestedProvider !== provider || usesHandoff || sameOriginPopupBlocked) {
      const handoff = topLevelHandoffUrl('login', { returnTo: currentLocation(), provider: requestedProvider });
      if (host === 'top-level') {
        window.location.assign(handoff);
        return;
      }
      // window.open stays in the click's synchronous call stack.
      if (!window.open(handoff, '_blank')) {
        tokenRecoveryAccount.current = recoveryAccount;
        setIssue({ kind: 'popupBlocked', message: POPUP_BLOCKED_TAB });
        return;
      }
      handoffRetryArmed.current = true;
      return;
    }

    pendingInteraction.current = true;
    setIsSigningIn(true);
    try {
      if (popupMode && bridge && !popupFallback.current) {
        const result = recoveryAccount
          ? await instance.acquireTokenPopup({ scopes: loginRequest.scopes, account: recoveryAccount, redirectUri: bridge })
          : await instance.loginPopup({ scopes: loginRequest.scopes, redirectUri: bridge });
        if (result.account) activateAccount(result.account);
      } else if (recoveryAccount) {
        await instance.acquireTokenRedirect({
          ...loginRequest,
          redirectUri,
          account: recoveryAccount,
          redirectStartPage: window.location.href
        });
      } else {
        await instance.loginRedirect({ ...loginRequest, redirectUri, redirectStartPage: window.location.href });
      }
    } catch (error) {
      tokenRecoveryAccount.current = recoveryAccount;
      const failure = classifyAuthError(error);
      if (failure === 'cancelled') return;
      if (failure === 'popupBlocked') {
        // Never retry automatically: the next click uses the fallback.
        popupFallback.current = true;
        setIssue({
          kind: 'popupBlocked',
          message: host === 'top-level'
            ? 'Your browser blocked the Microsoft sign-in window. Select Sign in again to continue in this tab.'
            : 'Your browser blocked the Microsoft sign-in window. Select Sign in again to open sign-in in a new tab.'
        });
        return;
      }
      setIssue({ kind: 'unexpected', message: 'Microsoft sign-in failed. Please try again.' });
    } finally {
      pendingInteraction.current = false;
      setIsSigningIn(false);
      setReady(true);
    }
  }, [activateAccount, bridge, inProgress, instance, loginRequest, popupMode, provider, redirectUri, usesHandoff]);

  const logout = React.useCallback(async () => {
    const current = accountRef.current;
    if (!current || inProgress !== InteractionStatus.None || pendingInteraction.current) return;
    pendingInteraction.current = true;
    setIssue(null);
    setIsSigningOut(true);
    suppressAccountRestore.current = true;
    tokenRecoveryAccount.current = null;
    try {
      if (usesHandoff) {
        // Sign this view out, then end the Microsoft session in a top-level tab.
        const opened = window.open(topLevelHandoffUrl('logout'), '_blank');
        await instance.clearCache({ account: current });
        if (!opened) {
          setIssue({
            kind: 'popupBlocked',
            message: 'You are signed out here, but your browser blocked the Microsoft sign-out tab. Allow popups to end your Microsoft session too.'
          });
        }
      } else if (popupMode) {
        await instance.logoutPopup({
          account: current,
          postLogoutRedirectUri: redirectUri,
          mainWindowRedirectUri: redirectUri
        });
      } else {
        await instance.logoutRedirect({ account: current, postLogoutRedirectUri: redirectUri });
      }
    } catch (error) {
      const failure = classifyAuthError(error);
      if (failure !== 'cancelled') {
        setIssue(
          failure === 'popupBlocked'
            ? {
                kind: 'unexpected',
                message: 'Your local session was cleared, but your browser blocked Microsoft sign-out.'
              }
            : {
                kind: 'unexpected',
                message: 'Your local session was cleared, but Microsoft sign-out could not be completed.'
              }
        );
      }
    } finally {
      activateAccount(null);
      pendingInteraction.current = false;
      setIsSigningOut(false);
      setReady(true);
    }
  }, [activateAccount, inProgress, instance, popupMode, redirectUri, usesHandoff]);

  const getAccessToken = React.useCallback(
    async (scopes: readonly string[]) => {
      const current = accountRef.current;
      if (!current) return null;
      try {
        const result = await instance.acquireTokenSilent({
          ...silentOptions,
          account: current,
          scopes: [...scopes]
        });
        return result.accessToken || null;
      } catch (error) {
        if (
          error instanceof InteractionRequiredAuthError ||
          classifyAuthError(error) === 'interactionRequired'
        ) {
          // The person is signed in; Microsoft wants approval for these extra permissions.
          throw new MicrosoftConsentRequiredError(scopes);
        }
        throw new Error('A Microsoft access token could not be acquired.');
      }
    },
    [instance, silentOptions]
  );

  const requestMicrosoftConsent = React.useCallback(
    async (kind: MicrosoftConsentKind): Promise<MicrosoftConsentResult> => {
      const current = accountRef.current;
      if (!current) return 'failed';
      if (inProgress !== InteractionStatus.None || pendingInteraction.current) return 'busy';
      const scopes = [...MICROSOFT_CONSENT_SCOPES[kind]];

      if (usesHandoff || (host === 'same-origin-frame' && popupFallback.current)) {
        // Approval is not sign-in: the frame keeps its account and retries silently afterwards.
        const handoff = topLevelHandoffUrl('graphConsent', { returnTo: currentLocation(), consent: kind });
        return window.open(handoff, '_blank') ? 'pending' : 'blocked';
      }

      pendingInteraction.current = true;
      try {
        if (popupMode && bridge && !popupFallback.current) {
          const result = await instance.acquireTokenPopup({ scopes, account: current, redirectUri: bridge });
          return result.accessToken ? 'granted' : 'failed';
        }
        await instance.acquireTokenRedirect({
          scopes,
          account: current,
          redirectUri,
          redirectStartPage: window.location.href
        });
        return 'pending';
      } catch (error) {
        const failure = classifyAuthError(error);
        if (failure === 'cancelled') return 'cancelled';
        if (failure === 'popupBlocked') {
          // The next click uses the tab or redirect fallback.
          popupFallback.current = true;
          return 'blocked';
        }
        return 'failed';
      } finally {
        pendingInteraction.current = false;
      }
    },
    [bridge, inProgress, instance, popupMode, redirectUri, usesHandoff]
  );

  const getAzureDevOpsAccessToken = React.useCallback(async () => {
    const current = accountRef.current;
    if (!current) throw new AzureDevOpsTokenError('authentication_expired');

    return acquireAzureDevOpsAccessToken({
      instance,
      account: current,
      silentRequest: silentOptions,
      requestInteractiveConsent: async () => {
        if (inProgress !== InteractionStatus.None || pendingInteraction.current) return false;
        if (usesHandoff || (host === 'same-origin-frame' && popupFallback.current)) {
          // Consent is not sign-in: the frame keeps its account, and the next
          // Azure DevOps action renews silently, so no focus retry is armed.
          const handoff = topLevelHandoffUrl('devopsConsent', { returnTo: currentLocation() });
          return Boolean(window.open(handoff, '_blank'));
        }
        if (popupMode && bridge && !popupFallback.current) {
          pendingInteraction.current = true;
          try {
            const result = await instance.acquireTokenPopup({
              scopes: [...AZURE_DEVOPS_SCOPES],
              account: current,
              redirectUri: bridge
            });
            return result.accessToken || false;
          } catch (error) {
            // A popup opened after the silent attempt can be blocked; the next
            // attempt uses the redirect (top-level) or tab (frame) fallback.
            if (classifyAuthError(error) === 'popupBlocked') {
              popupFallback.current = true;
              return false;
            }
            throw error;
          } finally {
            pendingInteraction.current = false;
          }
        }
        await instance.acquireTokenRedirect({
          scopes: [...AZURE_DEVOPS_SCOPES],
          redirectUri,
          account: current,
          redirectStartPage: window.location.href
        });
        return true;
      }
    });
  }, [bridge, inProgress, instance, popupMode, redirectUri, silentOptions, usesHandoff]);

  const value = React.useMemo<AuthContextValue>(
    () => ({
      enabled: true,
      ready,
      isAuthenticated: Boolean(account),
      account: account ? accountDisplay(account) : null,
      issue,
      isSigningIn,
      isSigningOut,
      recoveryUrl: window.location.href,
      provider,
      externalProviderAvailable: externalEntraConfigurationComplete,
      getAccessToken,
      getAzureDevOpsAccessToken,
      requestMicrosoftConsent,
      login,
      logout,
      clearIssue: () => setIssue(null)
    }),
    [
      account,
      getAccessToken,
      getAzureDevOpsAccessToken,
      requestMicrosoftConsent,
      isSigningIn,
      isSigningOut,
      issue,
      login,
      logout,
      provider,
      ready
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function MsalRoot({
  instance,
  provider,
  children
}: {
  instance: IPublicClientApplication;
  provider: AuthProviderKind;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  // Layout effects run before MsalProvider's redirect handling effect, so the
  // post-redirect move to the start page is an in-app route change.
  React.useLayoutEffect(() => {
    instance.setNavigationClient(new RouterNavigationClient(navigate));
  }, [instance, navigate]);

  return (
    <MsalProvider instance={instance}>
      <MsalAuthBridge provider={provider}>{children}</MsalAuthBridge>
    </MsalProvider>
  );
}

export function AuthProvider({
  instance,
  provider = 'workforce',
  children
}: {
  instance: IPublicClientApplication | null;
  provider?: AuthProviderKind;
  children: React.ReactNode;
}) {
  const e2eIdentity = readE2eBrowserIdentity();
  if (e2eIdentity) {
    return <E2eAuthBridge identity={e2eIdentity}>{children}</E2eAuthBridge>;
  }
  if (!instance) return <>{children}</>;

  return (
    <MsalRoot instance={instance} provider={provider}>
      {children}
    </MsalRoot>
  );
}
